import { useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useDisplayCategoryUpdateMutation } from '@occc-app/dataAccessLayer/displayCategory/mutations';

import { useGetDisplayCategoryById } from '@occc-app/dataAccessLayer/displayCategory/queries';
import { UpdateDisplayCategoryPage } from './UpdateDisplayCategoryPage';

import {
  mapBannersIdsToAutocompleteOptions,
  parseBannerIds,
  routeCreator,
  getShouldLazyLoadBannersForMutation,
} from '../utils';
import { DisplayCategoryFormValues } from '../types';
import { validateDisplayCategory } from '../validation';

export const UpdateDisplayCategoryPageContainer = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();

  const {
    loading,
    error,
    data: { bannerConfigurations, displayCategory },
  } = useGetDisplayCategoryById(id);
  const loaded = !!displayCategory;

  const initialValues: DisplayCategoryFormValues = useMemo(() => {
    const bannerIds = displayCategory?.bannerIds || [];
    const hasExternalSource = bannerIds.length > 0;

    return {
      name: displayCategory?.name || '',
      rank: displayCategory?.rank || 0,
      categoryRestrictions: displayCategory?.categoryRestrictions ?? 'None',
      banners: mapBannersIdsToAutocompleteOptions({
        bannerList: bannerConfigurations || null,
        bannerIds,
      }),
      shouldLazyLoadBanners: hasExternalSource
        ? displayCategory?.shouldLazyLoadBanners ?? false
        : undefined,
    };
  }, [bannerConfigurations, displayCategory]);

  const onCompleted = () => navigate(routeCreator.details(id));
  const [updateDisplayCategoryMutation] = useDisplayCategoryUpdateMutation({ onCompleted });

  const update = (values: DisplayCategoryFormValues) => {
    const { name, rank, banners, categoryRestrictions, shouldLazyLoadBanners } = values;
    const isNoCategoryRestrictions = !categoryRestrictions || categoryRestrictions === 'None';
    const bannerIds = parseBannerIds(banners) || [];

    if (!name) {
      return;
    }

    return updateDisplayCategoryMutation({
      variables: {
        input: {
          name,
          categoryRestrictions: isNoCategoryRestrictions ? null : categoryRestrictions,
          rank: rank || 0,
          id,
          bannerIds,
          shouldLazyLoadBanners: getShouldLazyLoadBannersForMutation(
            bannerIds,
            shouldLazyLoadBanners,
          ),
        },
      },
    });
  };

  return (
    <UpdateDisplayCategoryPage
      update={update}
      validate={validateDisplayCategory}
      initialValues={initialValues}
      loading={loading}
      loaded={loaded}
      error={error}
    />
  );
};
